/*   Common Functions
 ********************************************* */
/**
 * Parses a time string in the format "HH:MM:SS" or "HH:MM" into an object with hours, minutes, and seconds.
 * @param {string} timeString - The time string to parse.
 * @returns {Object} An object containing hours, minutes, and seconds.
 */
function parseTime(timeString) {
  // Split the time string by colon and convert each part to a number
  // If seconds are not provided, default to 0
  const parts = timeString.split(":");
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  return { hours, minutes };
}

/** * Compares two time strings in "HH:MM" format.
 * @param {string} t1 - The first time string.
 * @param {string} t2 - The second time string.
 * @returns {boolean} True if t1 is earlier than t2, false otherwise.
 * */
function isFirstInputEarlier(t1, t2) {
  const [hours1, minutes1] = t1.split(":").map(Number);
  const [hours2, minutes2] = t2.split(":").map(Number);

  // Return True if hours and minutes are same for both inputs
  if (hours1 === hours2 && minutes1 === minutes2) {
    return true;
  }

  // Compare hours first
  if (hours1 < hours2) {
    return true;
  } else if (hours1 > hours2) {
    return false;
  } else {
    // If hours are equal, compare minutes
    return minutes1 < minutes2;
  }
}

/**
 * @param {month-year} value eg:08-2024
 * @returns array of dates of that month
 */
function getDatesArrByMonth(value, pad = false) {
  if (!value) {
    return;
  }
  const [month, year] = value?.split("-").map(Number);
  const date = new Date(year, month - 1, 1);
  const dates = [];
  const paddedMonth = pad ? String(month).padStart(2, "0") : month;
  while (date.getMonth() === month - 1) {
    const day = date.getDate();
    dates.push(`${paddedMonth}-${day}-${year}`);
    date.setDate(date.getDate() + 1);
  }
  return dates;
}

function calculateLateHours(relaxationTime, inTime) {
  const t1 = parseTime(relaxationTime);
  const t2 = parseTime(inTime);

  const startDate = new Date();
  const endDate = new Date();

  startDate.setHours(t1.hours, t1.minutes, 0);
  endDate.setHours(t2.hours, t2.minutes, 0);

  const differenceInMilliseconds = endDate - startDate;

  const millisecondsInHour = 1000 * 60 * 60;
  const millisecondsInMinute = 1000 * 60;

  // Fix: Don't use the conditional checks that were causing the problem
  // Instead, simply calculate the time difference if it's positive
  if (differenceInMilliseconds <= 0) {
    return { hours: 0, minutes: 0, seconds: 0 };
  }

  const hours = Math.floor(differenceInMilliseconds / millisecondsInHour);
  const minutes = Math.floor(
    (differenceInMilliseconds % millisecondsInHour) / millisecondsInMinute
  );

  return { hours, minutes, seconds: 0 };
}

/** * Gets the name of the day from a given date string. * @param {string} dateString - The date string (e.g., "11-14-2024"). @param {number} sliceLength - The length to slice the date string. * @returns {string} The name of the day (e.g., "Thursday"). */
function getDayNameByDate(dateString, sliceLength = false) {
  // get Day name irrespective of the current timezone
  // check for correct dateString format
  if (!dateString) {
    return "";
  }

  const date = new Date(new Date(dateString).toISOString());
  const options = { weekday: "long" }; // Options to format the day name
  let dayName = new Intl.DateTimeFormat("en-US", options).format(date);
  if (sliceLength) {
    dayName = dayName.slice(0, sliceLength);
  }
  return dayName;
}

/**
 * Calculates the time difference between two time strings.
 * If the second time is later than the first, the result is negative.
 * @param {string} time1 - The first time string.
 * @param {string} time2 - The second time string.
 * @returns {Object} An object containing the difference in hours, minutes, and seconds.
 */

function calculateTimeDifference(time1, time2) {
  const t1 = parseTime(time1); // Parse the first time
  const t2 = parseTime(time2); // Parse the second time
  const date1 = new Date(); // Create a Date object for the first time
  const date2 = new Date(); // Create a Date object for the second time

  // Set the hours, minutes, and seconds for both Date objects
  date1.setHours(t1.hours, t1.minutes);
  date2.setHours(t2.hours, t2.minutes);

  // Calculate the difference in milliseconds between the two times
  const differenceInMilliseconds = date1 - date2;
  const millisecondsInHour = 1000 * 60 * 60; // Number of milliseconds in an hour
  const millisecondsInMinute = 1000 * 60; // Number of milliseconds in a minute

  // Determine if the difference is negative
  const isNegative = differenceInMilliseconds < 0;
  // Calculate the absolute difference in milliseconds
  const absoluteDifference = Math.abs(differenceInMilliseconds);

  // Calculate the hours, minutes, and seconds from the absolute difference
  const hours = Math.floor(absoluteDifference / millisecondsInHour);
  let minutes = Math.floor(
    (absoluteDifference % millisecondsInHour) / millisecondsInMinute
  );

  // Apply the negative sign to the hours and minutes if the difference is negative
  if (isNegative) {
    return { hours: -hours, minutes: -(minutes + 1) };
  }

  return { hours, minutes: minutes };
}

/** * Converts a time object with hours and minutes into decimal hours.
 * @param {Object} timeObj - An object containing hours and minutes.
 *  @param {number} timeObj.hours - The number of hours.
 * @param {number} timeObj.minutes - The number of minutes.
 * @returns {number} The time in decimal hours.
 * **/

function convertToDecimalHours(timeObj) {
  const { hours, minutes } = timeObj;
  // Destructure hours and minutes from the time object
  const decimalHours = hours + minutes / 60; // Convert minutes to decimal and add to hours
  let isNegative =
    Math.sign(hours) === -1 || Math.sign(minutes) === -1 ? true : false;
  return isNegative ? decimalHours * -1 : decimalHours;
}

//  Calculate Total Expected Hours
const getTotalHours = (userInTime, userOutTime) => {
  const hours = calculateTimeDifference(userOutTime, userInTime);

  let totalExpectedHoursInDecimal = parseFloat(
    convertToDecimalHours(hours).toFixed(2)
  );

  return totalExpectedHoursInDecimal;
};

const getTimeWithRelaxation = (
  time,
  userRelaxationMinutes,
  applyUserLateTimeRule,
  deviceRelaxationMinutes
) => {
  // Apply User Relaxation Minutes if available
  let relaxationMinutes = deviceRelaxationMinutes;

  if (applyUserLateTimeRule) {
    relaxationMinutes = userRelaxationMinutes || 0;
  }

  // calculate inTime with relaxation minutes
  let relaxationTimeInHours = Math.floor(relaxationMinutes / 60);
  let relaxationTimeInRemainingMinutes = relaxationMinutes % 60;

  let relaxationTime = `${relaxationTimeInHours}:${relaxationTimeInRemainingMinutes}`;

  let relaxationTimeObj = parseTime(relaxationTime);

  let TimeWithRelaxation = new Date();
  TimeWithRelaxation.setHours(
    relaxationTimeObj.hours + parseInt(time.split(":")[0]),
    relaxationTimeObj.minutes + parseInt(time.split(":")[1]),
    0
  );

  let inTimeWithRelaxationFormatted = `${TimeWithRelaxation.getHours()}:${TimeWithRelaxation.getMinutes()}`;
  return inTimeWithRelaxationFormatted;
};

const getCurTimeOfDeviceTimeZone = (selectedDevice) => {
  if (!selectedDevice) return "Select a device";
  if (!selectedDevice?.timezone) return "Select a device & timezone";

  const date = new Date();
  const options = {
    timeZone: selectedDevice?.timezone,
    hour: "2-digit",
    minute: "2-digit",
  };
  return date.toLocaleTimeString([], options);
};

// Calculate Late Time
const getTotalLateTime = (
  inTime,
  outTime,
  userInTime,
  userOutTime,
  userRelaxationMinutes,
  userRules,
  selectedDevice
) => {
  let totalLateTimeValue = 0.0;

  let applyUserLateTimeRule = userRules?.lateTime?.isChecked;

  const inTimeWithRelaxationFormatted = getTimeWithRelaxation(
    userInTime,
    userRelaxationMinutes,
    applyUserLateTimeRule,
    selectedDevice?.relaxationMinutes
  );

  // check if user is early then inTimeWithRelaxationFormatted
  let isEarly = isFirstInputEarlier(inTime, inTimeWithRelaxationFormatted);
  // If user is early then return 0.0

  let calculatedInTime = isEarly ? userInTime : inTime;

  let totalExpectedHours = getTotalHours(userInTime, userOutTime);
  let totalHoursWorked = getTotalHours(calculatedInTime, outTime);

  // get total late time only if totalHoursWorked is less than totalExpectedHours
  if (totalHoursWorked < totalExpectedHours) {
    return (totalExpectedHours - totalHoursWorked).toFixed(2);
  }
  return 0.0;

  // // If user is early then return 0.0

  // if (isEarly) {
  //   return 0.0;
  // }

  // const timeDifference = calculateTimeDifference(inTime, userInTime);
  // // get decimal hours from timeDifference
  // const _decimalHours = convertToDecimalHours(timeDifference);

  // totalLateTimeValue = parseFloat(_decimalHours.toFixed(2)) || 0.0;

  // return totalLateTimeValue;
};

const getTotalOvertime = (
  inTime,
  outTime,
  userInTime,
  userOutTime,
  overTimeAfterMinutes,
  userRules,
  selectedDevice
) => {
  let applyUserOverTimeRule = userRules?.overTime?.isChecked;

  const outTimeWithRelaxationFormatted = getTimeWithRelaxation(
    userOutTime,
    overTimeAfterMinutes,
    applyUserOverTimeRule,
    selectedDevice?.overtimeAfterMinutes
  );

  let totalOverTimeValue = 0.0;
  const totalHoursWorked = getTotalHours(inTime, outTime);
  const totalExpectedHours = getTotalHours(userInTime, userOutTime);

  let totalExpectedHoursWithRelaxation = getTotalHours(
    userInTime,
    outTimeWithRelaxationFormatted
  );

  // If overtime rule is applied, Calculate overtime after expected hours + overtimeAfterMinutes
  // Calculate overtime only if totalHoursWorked is greater than totalExpectedHours
  if (totalHoursWorked > totalExpectedHoursWithRelaxation) {
    // Calculate overtime after expected hours + overtimeAfterMinutes
    const overtime = totalHoursWorked - totalExpectedHours;
    totalOverTimeValue = parseFloat(overtime.toFixed(2)) || 0.0;
  }

  // if (!applyOverTimeRule) {
  //   // If overtime rule is not applied,  then return difference of outTime and userOutTime in decimal hours
  //   const overtime = getTotalHours(userOutTime, outTime);
  //   totalOverTimeValue = parseFloat(overtime.toFixed(2)) || 0.0;
  // }

  return totalOverTimeValue || 0.0;
};

/*   Day Office Functions
 ********************************************* */

/*   Night Office Functions
 ********************************************* */
/**
 * Calculates the difference in hours and minutes from a given time to the end of the day.
 * @param {string} timeString - The time string in "HH:MM" format.
 * @returns {Object} An object containing the difference in hours and minutes.
 */

function calculateTimeDifferenceToEndOfDay(timeString) {
  // Parse the input time string
  const [inputHours, inputMinutes] = timeString.split(":").map(Number);

  // Create Date objects for the input time and end of the day
  const currentTime = new Date();
  currentTime.setHours(inputHours, inputMinutes, 0, 0); // Ensure seconds and milliseconds are set to 0

  const endOfDay = new Date(currentTime);
  endOfDay.setHours(24, 0, 0, 0); // Set to midnight of the next day

  // Calculate the difference in milliseconds
  const differenceInMilliseconds = endOfDay - currentTime;
  const millisecondsInHour = 1000 * 60 * 60;
  const millisecondsInMinute = 1000 * 60;

  // Calculate hours and minutes from the difference
  const hours = Math.floor(differenceInMilliseconds / millisecondsInHour);
  const minutes = Math.floor(
    (differenceInMilliseconds % millisecondsInHour) / millisecondsInMinute
  );

  return { hours, minutes };
}

function calculateHoursWorkedNight(inTime, outTime) {
  const t1 = parseTime(inTime);
  const t2 = parseTime(outTime);

  const startDate = new Date();
  const endDate = new Date();

  startDate.setHours(t1.hours, t1.minutes, t1.seconds);
  endDate.setHours(t2.hours, t2.minutes, t2.seconds);

  const differenceInMilliseconds = endDate - startDate;

  const millisecondsInHour = 1000 * 60 * 60;
  const millisecondsInMinute = 1000 * 60;
  const millisecondsInSecond = 1000;

  const hours =
    t1.hours === t2.hours
      ? 0
      : Math.floor(differenceInMilliseconds / millisecondsInHour);
  // const hours = Math.floor(differenceInMilliseconds / millisecondsInHour);
  const minutes = Math.floor(
    (differenceInMilliseconds % millisecondsInHour) / millisecondsInMinute
  );
  const seconds = Math.floor(
    (differenceInMilliseconds % millisecondsInMinute) / millisecondsInSecond
  );

  return { hours, minutes, seconds };
}

// Export all functions with named exports (ES Modules style)
export {
  isFirstInputEarlier,
  getDatesArrByMonth,
  parseTime,
  calculateTimeDifferenceToEndOfDay,
  calculateHoursWorkedNight,
  calculateLateHours,
  getDayNameByDate,
  calculateTimeDifference,
  convertToDecimalHours,
  getTotalHours,
  getTotalLateTime,
  getTotalOvertime,
  getTimeWithRelaxation,
  getCurTimeOfDeviceTimeZone,
};
